/**
 * Iter 96 — Native platform abstraction layer.
 *
 * Single choke-point for "are we a real mobile app or a browser tab?" so the
 * rest of the codebase never imports Capacitor directly. In the browser every
 * helper degrades to a no-op / web behavior, preserving 100% of the current
 * web functionality.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch (e) { return false; }
};

export const nativePlatform = () => {
  try { return Capacitor.getPlatform(); } catch (e) { return "web"; }
};

/**
 * Hook native lifecycle into the existing web event model. Native app
 * resume/pause is broadcast as window CustomEvents so existing systems
 * (AmbientMixer resume, MorningAutoStart checks) can react without knowing
 * about Capacitor:
 *   - "continua:app-active"   → app returned to foreground
 *   - "continua:app-inactive" → app went to background (audio keeps playing)
 */
export const initNativeLifecycle = async () => {
  if (!isNative()) return;
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appStateChange", ({ isActive }) => {
      try {
        window.dispatchEvent(new Event(isActive ? "continua:app-active" : "continua:app-inactive"));
      } catch (e) { /* ignore */ }
    });
    // Android hardware back button: fall back to history, never exit mid-session.
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) window.history.back();
    });
  } catch (e) { /* plugin unavailable — stay web-behaved */ }
};

/**
 * Foundation for native Morning Sessions: schedule a local notification at
 * (wake-up − 5 min) so the OS wakes Continua even if it was terminated
 * overnight. The in-app MorningAutoStart scheduler stays the primary path;
 * this is the native reliability layer on top. No-op on web.
 */
export const scheduleMorningNotification = async ({ wakeTime, title, body }) => {
  if (!isNative() || !wakeTime) return false;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return false;
    const [h, m] = String(wakeTime).split(":").map(Number);
    const at = new Date();
    at.setHours(h, m, 0, 0);
    at.setMinutes(at.getMinutes() - 5);
    if (at.getTime() <= Date.now()) at.setDate(at.getDate() + 1);
    await LocalNotifications.cancel({ notifications: [{ id: 9601 }] }).catch(() => {});
    await LocalNotifications.schedule({
      notifications: [{
        id: 9601,
        title: title || "Good morning",
        body: body || "Your companion is waking up beside you.",
        schedule: { at, allowWhileIdle: true },
      }],
    });
    return true;
  } catch (e) {
    return false;
  }
};

export const cancelMorningNotification = async () => {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.cancel({ notifications: [{ id: 9601 }] });
  } catch (e) { /* ignore */ }
};

/**
 * Iter 96 — Android foreground MEDIA service. Overnight playback is a core
 * feature; many Android OEMs kill backgrounded WebViews. While a session is
 * playing we hold a low-priority "playing softly" notification via a
 * mediaPlayback foreground service so the OS keeps Continua alive.
 * No-op on web and iOS (iOS uses the background-audio session instead).
 */
let _fgServiceRunning = false;

export const startPlaybackForegroundService = async (title) => {
  if (nativePlatform() !== "android" || _fgServiceRunning) return;
  try {
    const { ForegroundService } = await import("@capawesome-team/capacitor-android-foreground-service");
    try { await ForegroundService.requestPermissions(); } catch (e) { /* older Android — fine */ }
    await ForegroundService.startForegroundService({
      id: 9602,
      title: title || "Continua",
      body: "Session playing softly — safe to lock your phone.",
      smallIcon: "ic_launcher",
      serviceType: "mediaPlayback",
      silent: true,
    });
    _fgServiceRunning = true;
  } catch (e) { /* never block playback on service failure */ }
};

export const stopPlaybackForegroundService = async () => {
  if (nativePlatform() !== "android" || !_fgServiceRunning) return;
  _fgServiceRunning = false;
  try {
    const { ForegroundService } = await import("@capawesome-team/capacitor-android-foreground-service");
    await ForegroundService.stopForegroundService();
  } catch (e) { /* ignore */ }
};
